import { styled, Checkbox, FormControlLabel, Typography, Box, Paper, Divider } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { updateGlobalLoaderState } from '../../features/global/globalSlice';
import { PopUpModal } from '../../components/molecules/PopUpModal/PopUpModal';
import { Fragment, useState, useCallback, useEffect } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { Toast } from '../../components/molecules/Toast/Toast';
import { uipApiInstance } from '../../services';
import dayjs from 'dayjs';

export interface CreatePdfReportsModalProps {
    open: boolean;
    openPDFViewer: boolean;
    handleClose: () => void;
    handleSaveButtonAction: () => void;
    goalPortfolioMappings: {
        goalName: string;
        portfolioName: string;
        portfolioUrl?: string;
    }[];
    handleInvalidUrlToaster?: any;
    toShowFactSheetsMissingToast?: boolean;
    triggerBackendDownload?: any;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '16px',
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: 'none'
}));

const StyledRow = styled(Box)(() => ({
    display: 'flex',
    height: '46px',
    alignSelf: 'stretch',
    padding: '2px 24px',
    alignItems: 'center',
    '& > :first-of-type': {
        width: '250px',
        flexShrink: 0,
        paddingLeft: '24px'
    },
    '& > :last-child': {
        flex: 1
    }
}));

const StyledDivider = styled(Divider)(() => ({
    margin: '0 32px'
}));

const StyledFooter = styled(Box)(() => ({
    marginTop: '16px'
}));

export const CreatePdfReportsModal = ({
    open,
    handleClose,
    goalPortfolioMappings,
    openPDFViewer,
    handleInvalidUrlToaster,
    toShowFactSheetsMissingToast,
    triggerBackendDownload
}: CreatePdfReportsModalProps) => {
    const dispatch = useAppDispatch();

    const adviceClient = useAppSelector((state) => state.adviceClient);
    const [includeFactsheet, setIncludeFactsheet] = useState(true);
    const [pdfToast, setPdfToast] = useState({ open: false, generating: false });
    const { t } = useTranslation();

    useEffect(() => {
        if (!open) {
            setIncludeFactsheet(true);
        }
    }, [open]);

    const handleFactsheetDownload = useCallback(async () => {
        console.log('Factsheet download started');
        let timestamp = dayjs().format('MMDDYY');
        if (includeFactsheet) {
            try {
                const uniqueUrls = Array.from(
                    new Set(
                        goalPortfolioMappings.map((m) => m.portfolioUrl).filter((url): url is string => Boolean(url))
                    )
                );
                if (uniqueUrls.length === 0) return;
                const response = await uipApiInstance({
                    method: 'POST',
                    url: '/api/advice/mergefactsheets',
                    withCredentials: false,
                    data: {
                        links: uniqueUrls
                    },
                    responseType: 'blob'
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                // Option to download as file
                const link = document.createElement('a');
                link.href = url;
                link.download = `${adviceClient?.clientFirstName ?? ''}_${
                    adviceClient?.clientLastName ?? ''
                }_FS_${timestamp}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Option to open in new tab
                // window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            } catch (err) {
                console.error('Failed to merge factsheets:', err);
                if (err === 'No base64 PDF data provided') {
                    handleInvalidUrlToaster && handleInvalidUrlToaster();
                }
            }
        }
    }, [goalPortfolioMappings, includeFactsheet]);

    const handleBackendDownload = useCallback(async () => {
        handleClose();
        dispatch(updateGlobalLoaderState(true));
        setPdfToast({ open: true, generating: true });
        await Promise.all([handleFactsheetDownload(), triggerBackendDownload()]);
        setPdfToast({ open: false, generating: false });
        dispatch(updateGlobalLoaderState(false));
        includeFactsheet && toShowFactSheetsMissingToast && handleInvalidUrlToaster && handleInvalidUrlToaster();
    }, [handleFactsheetDownload]);

    useEffect(() => {
        if (!openPDFViewer) {
            setPdfToast({ open: false, generating: false });
        }
    }, [openPDFViewer]);

    const handleIncludeFactsheetCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncludeFactsheet(event.target.checked);
    };

    return (
        <>
            <PopUpModal
                open={open}
                titleBold
                boldCloseButton
                handleClose={handleClose}
                handleSaveButtonAction={handleBackendDownload}
                saveButtonEndIcon={<OpenInNewIcon fontSize="small" sx={{ color: 'common.white' }} />}
                title={t('CREATE_PDF_REPORTS_TITLE')}
                description={
                    <>
                        <Typography variant="body1" gutterBottom>
                            {t('CREATE_PDF_REPORTS_INFO')}
                        </Typography>
                        <StyledPaper elevation={0}>
                            <StyledRow>
                                <Typography variant="subtitle1" fontWeight={800}>
                                    {t('CREATE_PDF_REPORTS_TABLE_GOAL')}
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={800}>
                                    {t('CREATE_PDF_REPORTS_TABLE_MODEL')}
                                </Typography>
                            </StyledRow>
                            <StyledDivider />

                            {goalPortfolioMappings.map((mapping, index) => (
                                <Fragment key={index}>
                                    <StyledRow>
                                        <Typography>{mapping.goalName}</Typography>
                                        <Typography>{mapping.portfolioName}</Typography>
                                    </StyledRow>
                                    {index < goalPortfolioMappings.length - 1 && <StyledDivider />}
                                    {/* <Box pb={2}></Box> */}
                                </Fragment>
                            ))}
                        </StyledPaper>
                        <StyledFooter>
                            <Box>
                                <Typography gutterBottom fontWeight={500} sx={{ color: '#616161' }}>
                                    {t('CREATE_PDF_REPORTS_REPORT_OPTIONS')}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeFactsheet}
                                            onChange={handleIncludeFactsheetCheckbox}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: '#212121', fontWeight: 600 }}>
                                            {t('CREATE_PDF_REPORTS_INCLUDE_FACTSHEETS')}
                                        </Typography>
                                    }
                                />
                            </Box>
                        </StyledFooter>
                        {includeFactsheet && (
                            <Box mt={3}>
                                <Typography
                                    sx={{
                                        color: '#212121',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        display: 'inline-block'
                                    }}
                                >
                                    {t('CREATE_PDF_REPORTS_FACTSHEET_SHORT_DISCLAIMER')}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#212121',
                                        fontWeight: 400,
                                        fontSize: 12,
                                        display: 'block'
                                    }}
                                >
                                    {t('CREATE_PDF_REPORTS_FACTSHEET_LONG_DISCLAIMER')}
                                </Typography>
                            </Box>
                        )}
                    </>
                }
                saveButtonLabel={t('CREATE_PDF_REPORTS_CONTINUE_BTN')}
                cancelButtonLabel={t('BUTTON_CANCEL')}
                showTitle={true}
                showCancelButton={true}
                PaperProps={{
                    style: {
                        width: '783px'
                        // height: '810px'
                    }
                }}
            />
            {pdfToast.open && (
                <Toast
                    heading={t('PDF_BEING_GENERATED')}
                    type="alert"
                    messages={[]}
                    handleClose={() => setPdfToast({ open: false, generating: false })}
                    disabled={false}
                    isTimer={false}
                />
            )}
        </>
    );
};
